import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { GenerateButton } from '@/shared/ui/generate-button'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'

export const MessageTemplatesSettingsPage = () => {
  const [newTitle, setNewTitle] = useState('')
  const [newSubject, setNewSubject] = useState('')
  const [newBody, setNewBody] = useState('')
  const [newEditorKey, setNewEditorKey] = useState(0)
  const [editingId, setEditingId] = useState<
    Id<'messageTemplates'> | undefined
  >(undefined)
  const [editTitle, setEditTitle] = useState('')
  const [editSubject, setEditSubject] = useState('')
  const [editBody, setEditBody] = useState('')
  const [editEditorKey, setEditEditorKey] = useState(0)

  const { data: templates = [] } = useQuery(
    convexQuery(api.features.org.message_templates.listMyMessageTemplates, {}),
  )

  const { mutateAsync: createTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.message_templates.createMyMessageTemplate,
    ),
  })

  const { mutateAsync: updateTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.message_templates.updateMyMessageTemplate,
    ),
  })

  const { mutateAsync: deleteTemplate } = useMutation({
    mutationFn: useConvexMutation(
      api.features.org.message_templates.deleteMyMessageTemplate,
    ),
  })

  const { mutateAsync: generateBody, isPending: isGeneratingNewBody } =
    useMutation({
      mutationFn: useConvexAction(
        api.features.jobs.generate_text_fields.generateMyMessageTemplateBody,
      ),
    })

  const { mutateAsync: generateEditBody, isPending: isGeneratingEditBody } =
    useMutation({
      mutationFn: useConvexAction(
        api.features.jobs.generate_text_fields.generateMyMessageTemplateBody,
      ),
    })

  const handleCreate = async (): Promise<void> => {
    const title = newTitle.trim()
    const subject = newSubject.trim()
    const body = newBody.trim()
    if (!title || !subject || !body) return
    await createTemplate({ title, subject, body })
    setNewTitle('')
    setNewSubject('')
    setNewBody('')
    setNewEditorKey((k) => k + 1)
  }

  const handleStartEdit = (
    id: Id<'messageTemplates'>,
    title: string,
    subject: string,
    body: string,
  ): void => {
    setEditingId(id)
    setEditTitle(title)
    setEditSubject(subject)
    setEditBody(body)
    setEditEditorKey((k) => k + 1)
  }

  const handleSaveEdit = async (): Promise<void> => {
    if (!editingId) return
    const title = editTitle.trim()
    const subject = editSubject.trim()
    const body = editBody.trim()
    if (!title || !subject || !body) return
    await updateTemplate({
      templateId: editingId,
      title,
      subject,
      body,
    })
    setEditingId(undefined)
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <h2 className="text-base font-semibold text-primary">
          Message templates
        </h2>
        <p className="mt-1 text-sm text-primary/60">
          Create reusable email templates. Use{' '}
          <code className="rounded bg-primary/5 px-1 text-xs">
            {'{{candidateName}}'}
          </code>{' '}
          and{' '}
          <code className="rounded bg-primary/5 px-1 text-xs">
            {'{{jobTitle}}'}
          </code>{' '}
          as placeholders.
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
        <h3 className="text-sm font-medium text-primary">New template</h3>
        <Input
          placeholder="Template title..."
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <Input
          placeholder="Email subject..."
          value={newSubject}
          onChange={(e) => setNewSubject(e.target.value)}
        />
        <div className="flex justify-end">
          <GenerateButton
            isPending={isGeneratingNewBody}
            disabled={!newTitle.trim() || !newSubject.trim()}
            onClick={() =>
              void generateBody({
                title: newTitle,
                subject: newSubject,
              }).then((html) => {
                setNewBody(html)
                setNewEditorKey((k) => k + 1)
              })
            }
          />
        </div>
        <RichTextEditor
          key={newEditorKey}
          initialContent={newBody}
          onChange={setNewBody}
          placeholder="Email body... Use {{candidateName}} and {{jobTitle}}"
        />
        <div>
          <Button
            size="sm"
            onClick={() => void handleCreate()}
            disabled={!newTitle.trim() || !newSubject.trim() || !newBody.trim()}
          >
            Create template
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <p className="text-sm text-primary/40">No message templates yet.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {templates.map((template) => (
            <li
              key={template._id}
              className="flex flex-col gap-3 rounded-lg border border-border px-4 py-3"
            >
              {editingId === template._id ? (
                <div className="flex flex-col gap-2">
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Title"
                  />
                  <Input
                    value={editSubject}
                    onChange={(e) => setEditSubject(e.target.value)}
                    placeholder="Subject"
                  />
                  <div className="flex justify-end">
                    <GenerateButton
                      isPending={isGeneratingEditBody}
                      disabled={!editTitle.trim() || !editSubject.trim()}
                      onClick={() =>
                        void generateEditBody({
                          title: editTitle,
                          subject: editSubject,
                        }).then((html) => {
                          setEditBody(html)
                          setEditEditorKey((k) => k + 1)
                        })
                      }
                    />
                  </div>
                  <RichTextEditor
                    key={editEditorKey}
                    initialContent={editBody}
                    onChange={setEditBody}
                    placeholder="Body"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => void handleSaveEdit()}>
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingId(undefined)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium text-primary">
                        {template.title}
                      </span>
                      <span className="text-xs text-primary/50">
                        {template.subject}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleStartEdit(
                            template._id,
                            template.title,
                            template.subject,
                            template.body,
                          )
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void deleteTemplate({ templateId: template._id })
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <div
                    className="text-sm text-primary/70 [&_ol]:list-decimal [&_ol]:pl-4 [&_ul]:list-disc [&_ul]:pl-4"
                    dangerouslySetInnerHTML={{ __html: template.body }}
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
